import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { environment } from '@environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { PlayWinRepeatComponent } from '@app/shared/components/play-win-repeat/play-win-repeat.component';

import { LocalStorageService } from '@core/services/util/local-storage.service';
import { SessionStorageService } from '@core/services/util/session-storage.service';
import { StorageService } from '@core/services/util/storage.service';
import { DeviceService } from '@core/services/util/device.service';
import { CustomizationUtilService } from '@core/services/customization-util/customization-util.service';
import { MenuService } from '@core/services/menu/menu.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { CartService } from '@core/services/cart/cart.service';
import { GtmService } from '../analytics/gtm.service';
import { ToastrService } from '../notification/toastr.service';
import { UtilityService } from '@core/services/util/utility.service';

import { ActionType, Banner } from '@app/shared/models/api-response/catalog-api-response';

export type BannerType =
    | 'mweb_new_user'
    | 'mweb_lapsed_user'
    | 'mweb_repeat_user'
    | 'mweb_offer'
    | 'pip'
    | 'landing_feature'
    | 'membership_category_strip'
    | 'live_tracking'
    | 'bulk_order'
    | 'credit_page'
    | 'referral_page';
export type BannerResponseType = 'object' | 'array_list';

@Injectable({
    providedIn: 'root'
})
export class BannerService {
    cache$ = {};
    outletData = {};
    constructor(
        private datePipe: DatePipe,
        private storageService: StorageService,
        private localStorage: LocalStorageService,
        private sessionStorage: SessionStorageService,
        private deviceService: DeviceService,
        private http: HttpClient,
        private router: Router,
        private outletService: OutletService,
        private menuService: MenuService,
        private cartService: CartService,
        private customizationUtil: CustomizationUtilService,
        private dialog: MatDialog,
        private gtmService: GtmService,
        private toastrService: ToastrService,
        private utilityService: UtilityService
    ) {
        this.outletService.state.subscribe(async data => {
            if (data && data.outlet) {
                this.outletData = data.outlet;
            }
        });
    }

    fetchBannersFromArray(banner_types: Array<string>, brand_id?: number) {
        const params = {
            'banner_types[]': banner_types || [],
            brand_id: brand_id || environment.brand.id,
            lat: this.storageService.get('latitude') || 0,
            lon: this.storageService.get('longitude') || 0,
            app_id: environment.brand.id,
            outlet_wise: true
        };
        this.outletService.getPreferredOutletParams(params);

        return new Promise(resolve => {
            this.http
                .get(`${environment.catalogUrl}/catalog/v1/banners`, { params })
                .toPromise()
                .then((data: any) => {
                    if (data.banners && Object.keys(data.banners).length) {
                        const banner = data.banners;
                        const resolution = this.deviceService.getResolution();
                        Object.keys(banner).forEach(data => {
                            banner[data] = banner[data].map((banner: any) => {
                                return {
                                    ...banner,
                                    image: environment.assetsUrl + `/horizontal-rectangle/${resolution}/banner/` + banner.id
                                };
                            });
                        });
                        return resolve(banner);
                    }
                    return resolve({});
                })
                .catch(error => {
                    if (error.status === 0) {
                        const networkConnection = (window.navigator as any).connection;
                        if (networkConnection) {
                            const { effectiveType, downlink, rtt } = networkConnection;
                            this.gtmService.logEvent('js_errors', {
                                event_label: 'Banner API',
                                event_value: `${effectiveType}-${downlink}-${rtt}`,
                                event_action: error?.message || 'API Failure'
                            });
                        }
                    }
                    return resolve({});
                });
        });
    }

    webBanner(banner: any) {
        return banner.platforms.indexOf('web') >= 0;
    }

    getBannerType(): string | null {
        const utm_campaign = this.sessionStorage.get('utm_campaign');

        if (utm_campaign) {
            return utm_campaign;
        }

        return null;
    }

    getBannerImages(banners) {
        if (!banners || !banners.length) {
            return [];
        }

        let results = [];

        // add birthday banner
        if (this._isCustomerBirthdayToday()) {
            const bday_banner = banners.filter((banner: any) => banner.banner_type === 'birthday');
            results = results.concat(bday_banner);
        }

        // add specific banner type
        const banner_type = this.getBannerType();
        if (banner_type) {
            const campaign = banners.filter((banner: any) => banner.banner_type === banner_type);
            results = results.concat(campaign);
        }

        // add common banner
        const common_banners = banners.filter((banner: any) => !banner.banner_type);
        results = results.concat(common_banners);

        return results;
    }

    getAnnouncementUrl(announcement: any, brand_id: number) {
        if (!announcement || !announcement.image_url) {
            return null;
        }

        //temporary addition so that local storage data gets removed
        this.localStorage.remove('announcement_img_url');

        const previouslyShownAnnouncement = this.sessionStorage.get(`announcement_img_url_${brand_id}`);

        if (previouslyShownAnnouncement && previouslyShownAnnouncement === announcement.image_url) {
            return null;
        }

        this.sessionStorage.set(`announcement_img_url_${brand_id}`, announcement.image_url);
        return announcement.image_url;
    }

    private _isCustomerBirthdayToday() {
        const today = this.datePipe.transform(new Date().getTime(), 'MM-dd');
        const customerBirthDate = this.storageService.get('birthday');

        if (customerBirthDate) {
            const isUserBirthdayToday = customerBirthDate.slice(5) === today;
            return isUserBirthdayToday;
        }

        return false;
    }

    showPlugInVideo(videoSrc: any) {
        const isVideoShown = !!this.sessionStorage.get('isVideoShown');
        // play only once in a session
        if (!videoSrc || isVideoShown) {
            return false;
        } else {
            const videoPlatingUrl = this.localStorage.get('videoPlating') || {};
            // Only twice in a lifetime of a video url
            if (!(videoSrc in videoPlatingUrl) || (videoSrc in videoPlatingUrl && videoPlatingUrl[videoSrc] < 2)) {
                return true;
            } else {
                return false;
            }
        }
    }

    superSaverAction(banner: any, source = 'banner') {
        if (!this.outletService.isOutletSelected()) {
            this.utilityService.openLocationSearchBar();
            return;
        }

        // banner, membership, category...
        const {
            id,
            override_action: { at: action_type, al: action_link, br: brand_id }
        } = banner;

        switch (action_type) {
            case ActionType.ProductLink:
                this.openCustomizationModel(brand_id, action_link);
                break;

            case ActionType.CategoryLink:
                this.navigateToCategory(brand_id, action_link);
                break;

            case ActionType.DeepLink:
                this.handleDeepLink(action_link);
                break;

            default:
                this.gtmService.logEvent('js_errors', {
                    event_label: 'Banner API',
                    event_value: `Invalid action type: ${action_type}, source: ${source}, id: ${id}`
                });
                break;
        }
    }

    navigateToCategory(brandId: any, categoryId: any): void {
        const isCurrentBrand = environment.brand.id === brandId;
        const queryParams = isCurrentBrand ? `category_id=${categoryId}` : `brand_id=${brandId}&category_id=${categoryId}`;

        this.router.navigateByUrl(`/menu?${queryParams}`);
    }

    async openCustomizationModel(brandId: number, actionLink: any) {
        let outletData: any;
        if (brandId) {
            outletData = await this.fetchOutletData(brandId);
        }

        if (!outletData) {
            this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            return;
        }

        if (this.cartService.multiBrandOrderAllowed(actionLink, {}, false)) {
            this.customizationUtil.openCustomizationModel({ product_id: actionLink }, outletData.menuModel);
        } else {
            this.navigateToProductCategory(brandId, actionLink, outletData);
        }
    }

    navigateToProductCategory(brandId: any, actionLink: any, outletData: any): void {
        const product = outletData.menuModel?.products?.[actionLink];

        if (product?.category_id) {
            this.storageService.set('brand_id', brandId);
            this.router.navigateByUrl(`/menu?brand_id=${brandId}&category_id=${product.category_id}`);
        }
    }

    async fetchOutletData(brandId: number) {
        const outletData = await this.outletService.getOutletData(brandId);
        return outletData?.outlet ? this.menuService.generateMenuModel(outletData.outlet) : null;
    }

    openActionLink(imageData: Banner, index: number) {
        this.logAnalyticsData(imageData, index);

        const { override_action, action_url, action_type, tracking_id } = imageData;

        if (override_action) {
            this.superSaverAction(imageData);
        } else if (action_url) {
            this.handleDeepLink(action_url, action_type, tracking_id);
        }
    }

    handleDeepLink(actionUrl: string, actionType?: string, trackingId?: string): void {
        if (actionUrl.includes('category')) {
            this.handleCategoryDeepLink(actionUrl);
            return;
        }

        if (actionUrl.includes('eatclubgames')) {
            this.openPlayAndWinDialog();
            return;
        }

        this.handleExternalLink(actionUrl, actionType, trackingId);
    }

    handleCategoryDeepLink(actionUrl: string) {
        try {
            if (!this.deviceService.isMobile()) {
                const brandId = this.utilityService.getUrlParameter(actionUrl, 'brand_id');

                if (brandId) {
                    this.storageService.set('brand_id', brandId);
                } else {
                    throw new Error('Brand id not present in category deep link');
                }

                this.router.navigateByUrl('/menu');
            } else {
                window.location.href = actionUrl;
            }
        } catch (error) {
            this.gtmService.logEvent('js_errors', {
                event_label: 'Banner API',
                event_value: `Invalid category URL: ${actionUrl}, ${error}`
            });
        }
    }

    handleExternalLink(actionUrl: string, actionType?: string, trackingId?: string): void {
        const finalUrl = this.buildUrlWithTracking(actionUrl, actionType, trackingId);
        window.location.href = finalUrl;
    }

    buildUrlWithTracking(url: string, actionType?: string, trackingId?: string): string {
        return actionType === 'offer_link' && trackingId ? `${url}&tracking_id=${trackingId}` : url;
    }

    logAnalyticsData(imageData: any, index: number) {
        const referrer = (this.router.url.split('/')[1] || 'home').split('?')[0];
        const bannerType = imageData.banner_type ? ', banner_type: ' + imageData.banner_type : '';
        this.gtmService.logEventTracking(referrer, 'banner_click', 'banner_index: ' + index + bannerType);
    }

    openPlayAndWinDialog() {
        const dialogConfig = new MatDialogConfig();
        const isMobileDevice = this.deviceService.isMobile();

        dialogConfig.data = {};
        dialogConfig.panelClass = 'play-win-repeat-dialog';

        if (isMobileDevice) {
            dialogConfig.width = '100%';
            dialogConfig.maxWidth = '100%';
            dialogConfig.height = '100%';
        } else {
            dialogConfig.width = '450px';
            dialogConfig.maxWidth = '450px';
            dialogConfig.height = '650px';
        }

        this.dialog.open(PlayWinRepeatComponent, dialogConfig);
    }
}
